import {Component} from "@angular/core";
import {DialogService} from "../bootstrap-modal/dialog.service";
import {DialogComponent} from "../bootstrap-modal/dialog.component";
import {SiteService} from "./site.service";
import {handleErrors} from "../misc/handle-errors.decorator";
import {FilteredList} from "../helpers/filtered-list";
import {exposeComponentInDebugMode} from "../misc/expose-component-in-debug-mode.decorator";
import {ITSMSiteModel} from "./itsm-site.model";
import {toggleLoading} from "../misc/loading.decorator";
import {JSONModel} from "../json.model";
import {HasuraGraphqlService} from "../graphql/hasura-graphql-service";
import {Query} from "../graphql/query-constant";

export interface ImportITSMSitesInputModel {
  projectId: string;
}

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_LIMIT = 5;
const uuid = require("uuid");

@Component({
  templateUrl: "./import-itsm-site-dialog.template.pug",
  providers: [
    SiteService,
    HasuraGraphqlService,
  ],
})
@exposeComponentInDebugMode()
export class ImportITSMSiteDialogComponent
  extends DialogComponent<ImportITSMSitesInputModel, boolean> implements ImportITSMSitesInputModel {
  projectId: string;
  error: Error;
  orderName: string;
  selectedCount: number = 0;

  itsmSites: FilteredList<ITSMSiteModel> = new FilteredList<ITSMSiteModel>({
    startingPageSize: 5,
    searchKeys: ["name"],
    formKeys: ["selected"],
  });

  loading: boolean = true;

  currentPage: number = DEFAULT_PAGE;
  pageSize: number = DEFAULT_PAGE_LIMIT;
  maxPages: number = 1;
  searchText: string = "";

  areaGroup: any;
  hasuraSiteResponse: any;

  static showImportITSMSitesDialogModal(dialogService: DialogService, input: ImportITSMSitesInputModel):
    Promise<boolean> {
    return dialogService.showDialog(ImportITSMSiteDialogComponent, input);
  }

  constructor(
    public dialogService: DialogService,
    private siteService: SiteService,
    private hasuraGqlService: HasuraGraphqlService,
  ) {
    super(dialogService);

    this.result = false;
  }

  cancel(): void {
    this.close();
  }

  @toggleLoading()
  @handleErrors()
  async importSites() {
    this.loading = true;
    const sites = this.itsmSites.items.filter(it => it.selected).map(it => it.itsmBuildingId);
    const insertSites = [];
    if (sites.length) {
      for (const itsmBuildingId of sites) {
        this.hasuraSiteResponse.filter(_site => _site.itsmBuildingId === itsmBuildingId).map((_site) => {
          const insertSiteObj = {
            id: uuid.v1(),
            name: _site.name,
            k2_name: _site.name,
            project_id: this.projectId,
            site_address_id: _site.id,
            toner_address_id: _site.id,
            service_address_id: _site.id,
            default_area_group_id: this.areaGroup && this.areaGroup.length > 0 ? this.areaGroup[0].id : null,
          };
          insertSites.push(insertSiteObj);
        });
      }

      if (insertSites && insertSites.length > 0) {
        Query.SAVE_SITE.variables.input = insertSites;
        const params = {
          projectId: this.projectId,
          query: Query.SAVE_SITE,
        };
        await this.siteService.insertHasuraSites(params);
      }
    }
    this.loading = false;
    this.result = true;
    this.close();
  }

  deselectAll(): void {
    this.itsmSites.forEachSearchFiltered((fg) => {
      fg.controls["selected"].setValue(false);
    });

    this.updateSelection();
  }

  selectAll(): void {
    this.itsmSites.forEachSearchFiltered((fg) => {
      fg.controls["selected"].setValue(true);
    });

    this.updateSelection();
  }

  @toggleLoading()
  @handleErrors()
  async ready() {
    Query.SITE_IMPORT.variables.projectId = this.projectId;
    const params = {
      projectId: this.projectId,
      query: Query.SITE_IMPORT,
      uiAction: "SITE-IMPORT",
    };
    const siteToImport = await this.hasuraGqlService.getResponseFromHasura(params);
    if (siteToImport && siteToImport.body && siteToImport.body.data &&
      siteToImport.body.data.project && siteToImport.body.data.project.length > 0) {
      const importedSite = siteToImport.body.data.project[0].associate_sites &&
        siteToImport.body.data.project[0].associate_sites.length > 0 ?
        siteToImport.body.data.project[0].associate_sites : [];

      this.areaGroup = siteToImport.body.data.project[0].area_groups &&
      siteToImport.body.data.project[0].area_groups.length > 0 ?
        siteToImport.body.data.project[0].area_groups : [];

      let country = siteToImport.body.data.project[0].country_id;
      const globalDealClassification  = siteToImport.body.data.project[0].global_deal_classification;
      const deployedCountry = siteToImport.body.data.project[0].global_deal_settings[0].c2c_deployment_country;
      if (globalDealClassification  === 'C2C') {
        country = deployedCountry
      }
      let itsmSites = siteToImport.body.data.project[0].sites &&
        siteToImport.body.data.project[0].sites.length > 0 ?
        siteToImport.body.data.project[0].sites : [];
      itsmSites = itsmSites.filter(_sites => _sites.bpId && _sites.country_id === country &&
         _sites.building_types.find(
           _types => _types === "Shipping",
         )).map(_site => ({
        id: _site.id,
        name: `${_site.itsmBuildingName} (${_site.itsmSiteName})`,
        bpId: _site.bpId,
        itsmBuildingId: _site.itsmBuildingId,
        project_id: this.projectId,
        address: _site.address,
        city: _site.city,
        region: _site.regionByRegion && _site.regionByRegion.name ? _site.regionByRegion.name : "",
        zipCode: _site.zipCode,
        itsmSiteId: _site.itsmSiteId,
        itsmSiteName: _site.itsmSiteName,
        itsmBuildingName: _site.itsmBuildingName,
        shippingLabel1: _site.shippingLabel1,
        shippingLabel2: _site.shippingLabel2,
      }));
      itsmSites = itsmSites.filter(_allSite => !importedSite.some(_importedSite =>
        _importedSite.site_address_id === _allSite.id));

      this.hasuraSiteResponse = itsmSites;

      const sites = ITSMSiteModel.fromList(itsmSites);
      JSONModel.reIndexList(sites);
      this.itsmSites.items = sites;
    }
    // const sites = await this.siteService.loadSitesFromITSMToImport(this.projectId);
    this.deselectAll();
  }

  updateSelection(): void {
    this.selectedCount = this.itsmSites.items.filter(it => it.selected).length;
  }
}
